import React, { Component } from 'react';
import { Box, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import BackendService from '../../../services/admin/BackendService';
import { StyledButton } from '../../styles/StyledButtons';
import { StyledInput } from '../../styles/StyledInputs';
import { StyledFieldRow } from '../../styles/styles';
import { ImpersonateDialog } from './ImpersonateDialog';
import {
  ContactFormFields,
  ContactFormProps,
  ContactFormState,
  SALUTATION_OPTIONS,
} from './contactFormManagerTypes';

export default class ContactFormManager extends Component<ContactFormProps, ContactFormState> {
  private fieldRefs: { [K in keyof ContactFormFields]: React.RefObject<HTMLInputElement> } = {
    firstName: React.createRef(),
    lastName: React.createRef(),
    email: React.createRef(),
    mobile: React.createRef(),
    dateOfBirth: React.createRef(),
    city: React.createRef(),
    country: React.createRef(),
    salutation: React.createRef(),
    custodian: React.createRef(),
  };

  constructor(props: ContactFormProps) {
    super(props);
    this.state = {
      formFields: {},
      initialFormFields: {},
      submitting: false,
      impersonateDialogOpen: false,
    };
  }

  componentDidMount() {
    this.loadFormData();
  }

  componentDidUpdate(prevProps: ContactFormProps) {
    if (this.props.contact?.id !== prevProps.contact?.id) {
      this.loadFormData();
    }
  }

  private loadFormData = () => {
    const { contact } = this.props;
    if (contact) {
      const formFields = {
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        mobile: contact.mobile || '',
        dateOfBirth: contact.dateOfBirth || '',
        city: contact.city || '',
        country: contact.country || '',
        salutation: contact.salutation || '',
        custodian: contact.custodian || false,
      };
      this.setState({ formFields, initialFormFields: formFields });
    }
  };

  private isFormDirty = (): boolean => {
    const { formFields, initialFormFields } = this.state;
    const keys = Object.keys(initialFormFields) as (keyof ContactFormFields)[];
    return keys.some((key) => formFields[key] !== initialFormFields[key]);
  };

  private handleFieldChange = (field: keyof ContactFormFields, value: string | boolean) => {
    this.setState((prevState) => ({
      formFields: {
        ...prevState.formFields,
        [field]: value,
      },
      // Clear error when user starts typing
      errors: {
        ...prevState.errors,
        [field]: false,
      },
    }));
  };

  private validateForm = (): boolean => {
    const { formFields } = this.state;
    const errors: { [K in keyof ContactFormFields]?: boolean } = {};
    let hasErrors = false;

    // Required field validations
    if (!formFields.firstName?.trim()) {
      errors.firstName = true;
      hasErrors = true;
    }
    if (!formFields.lastName?.trim()) {
      errors.lastName = true;
      hasErrors = true;
    }
    if (!formFields.email?.trim()) {
      errors.email = true;
      hasErrors = true;
    }

    // Email format validation
    if (formFields.email && !this.isValidEmail(formFields.email)) {
      errors.email = true;
      hasErrors = true;
    }

    if (hasErrors) {
      this.setState({ errors });
      this.scrollToFirstError();
    }

    return !hasErrors;
  };

  private isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  private scrollToFirstError = () => {
    const errorKeys = Object.keys(this.state.errors || {}) as (keyof ContactFormFields)[];
    if (errorKeys.length > 0) {
      const firstErrorKey = errorKeys[0];
      const ref = this.fieldRefs[firstErrorKey];
      if (ref && ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ref.current.focus();
      }
    }
  };

  private handleSave = async () => {
    if (!this.validateForm()) {
      return;
    }

    this.setState({ submitting: true });

    try {
      const service = new BackendService();
      const { contact } = this.props;
      const { formFields } = this.state;
      const contactData = { ...formFields, custodian: formFields.custodian ?? false };

      if (contact) {
        await service.updateContact(contact.id, contactData);
        const updated = await service.getContact(contact.id);
        this.props.onContactUpdate(updated);
        this.props.onSuccess('Contact updated successfully');
        this.setState({ initialFormFields: formFields });
      } else {
        const newContactId = await service.createContact(contactData);
        this.props.onSuccess('Contact created successfully');
        this.props.onContactCreated?.(newContactId);
      }
    } catch (error: any) {
      const action = this.props.contact ? 'update' : 'create';
      this.props.onError(`Failed to ${action} contact: ${error.message || 'Unknown error'}`);
    } finally {
      this.setState({ submitting: false });
    }
  };

  private handleImpersonate = () => {
    if (!this.props.contact) return;
    this.setState({ impersonateDialogOpen: true });
  };

  private renderField = (
    name: keyof ContactFormFields,
    label: string,
    options?: { required?: boolean; type?: string },
  ) => {
    const { formFields, errors } = this.state;
    const hasError = errors?.[name] || false;

    return (
      <StyledInput
        inputRef={this.fieldRefs[name]}
        label={label}
        value={formFields[name] || ''}
        onChange={(e) => this.handleFieldChange(name, e.target.value)}
        error={hasError}
        helperText={hasError ? `${label} is required` : ''}
        required={options?.required}
        type={options?.type || 'text'}
        InputLabelProps={options?.type === 'date' ? { shrink: true } : undefined}
        fullWidth
      />
    );
  };

  private renderSelectField = (
    name: keyof ContactFormFields,
    label: string,
    options: { value: string; label: string }[],
  ) => {
    const { formFields, errors } = this.state;
    const hasError = errors?.[name] || false;

    return (
      <StyledInput
        select
        label={label}
        value={formFields[name] || ''}
        onChange={(e) => this.handleFieldChange(name, e.target.value)}
        error={hasError}
        helperText={hasError ? `${label} is required` : ''}
        fullWidth
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </StyledInput>
    );
  };

  private renderCustodianCheckbox = () => {
    const { formFields } = this.state;

    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={formFields.custodian || false}
            onChange={(e) => this.handleFieldChange('custodian', e.target.checked)}
          />
        }
        label="Custodian"
      />
    );
  };

  render() {
    const { contact } = this.props;
    const { submitting } = this.state;

    const canSave = !submitting && (!contact || this.isFormDirty());
    const saveButtonLabel = submitting ? 'Saving...' : contact ? 'Save Changes' : 'Add Contact';

    return (
      <Box>
        {/* Editing an existing contact: Save Changes pairs with Impersonate at
            the top, since there's no form-bottom "submit" feel to preserve.
            Adding a new one: no Impersonate to pair it with, so it stays as a
            normal submit button under the fields instead. */}
        {contact && (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, mt: '10px', mb: 2 }}>
              <StyledButton
                noTopSpacing
                variant="contained"
                onClick={this.handleSave}
                disabled={!canSave}
              >
                {saveButtonLabel}
              </StyledButton>
              {contact.signedUp && (
                <StyledButton noTopSpacing buttonVariant="warning" onClick={this.handleImpersonate}>
                  Impersonate
                </StyledButton>
              )}
            </Box>
            {contact.signedUp && (
              <ImpersonateDialog
                open={this.state.impersonateDialogOpen}
                contact={contact}
                onClose={() => this.setState({ impersonateDialogOpen: false })}
                onError={this.props.onError}
              />
            )}
          </>
        )}
        <StyledFieldRow sx={{ gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            {this.renderSelectField('salutation', 'Salutation', SALUTATION_OPTIONS)}
          </Box>
          <Box sx={{ flex: 2 }}>
            {this.renderField('firstName', 'First Name', { required: true })}
          </Box>
          <Box sx={{ flex: 2 }}>
            {this.renderField('lastName', 'Last Name', { required: true })}
          </Box>
        </StyledFieldRow>

        <StyledFieldRow sx={{ gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            {this.renderField('email', 'Email', { required: true, type: 'email' })}
          </Box>
          <Box sx={{ flex: 1 }}>{this.renderField('mobile', 'Mobile')}</Box>
        </StyledFieldRow>

        <StyledFieldRow sx={{ gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>{this.renderField('city', 'City')}</Box>
          <Box sx={{ flex: 1 }}>{this.renderField('country', 'Country')}</Box>
          <Box sx={{ flex: 1 }}>
            {this.renderField('dateOfBirth', 'Date of Birth', { type: 'date' })}
          </Box>
        </StyledFieldRow>

        <StyledFieldRow>{this.renderCustodianCheckbox()}</StyledFieldRow>

        {!contact && (
          <Box sx={{ mt: 2 }}>
            <StyledButton variant="contained" onClick={this.handleSave} disabled={!canSave}>
              {saveButtonLabel}
            </StyledButton>
          </Box>
        )}
      </Box>
    );
  }
}
